import React from "react";
import { RefreshCw, Pencil, Check, X, Lock, Settings, Search } from "lucide-react";
import {
  listPuzzles,
  editPuzzle,
  regeneratePuzzle,
  generateBuffer,
  clearToken,
  apiError,
} from "./adminApi";
import AdminSettings from "./AdminSettings";

const statusStyle = {
  LIVE: "bg-[#7e9a70] text-white",
  SCHEDULED: "bg-[#eceae2] text-[#4a4a45]",
  COMPLETED: "bg-[#d7d6cf] text-[#75756f]",
};

function fmtDate(iso) {
  return new Date(iso + "T00:00:00").toLocaleDateString("en-US", {
    month: "short",
    day: "numeric",
    year: "numeric",
  });
}

function Check1({ ok, children }) {
  return (
    <span className={`inline-flex items-center gap-1 ${ok ? "text-[#5f7d50]" : "text-[#b0472f]"}`}>
      {ok ? <Check size={12} /> : <X size={12} />} {children}
    </span>
  );
}

function Row({ p, onChanged }) {
  const [editing, setEditing] = React.useState(false);
  const [divisor, setDivisor] = React.useState(p.divisor);
  const [secret, setSecret] = React.useState(p.secret);
  const [error, setError] = React.useState("");
  const [busy, setBusy] = React.useState(false);

  const locked = !p.editable; // LIVE or COMPLETED — permanently immutable

  const validLen = /^\d{5}$/.test(secret) && secret[0] !== "0";
  const divisible = validLen && Number(secret) % Number(divisor) === 0;

  const beginEdit = () => {
    setDivisor(p.divisor);
    setSecret(p.secret);
    setError("");
    setEditing(true);
  };

  const save = async () => {
    setError("");
    setBusy(true);
    try {
      await editPuzzle(p.puzzle_number, { divisor: Number(divisor), secret });
      setEditing(false);
      onChanged();
    } catch (e) {
      setError(apiError(e));
    } finally {
      setBusy(false);
    }
  };

  const regen = async () => {
    setBusy(true);
    setError("");
    try {
      await regeneratePuzzle(p.puzzle_number);
      onChanged();
    } catch (e) {
      setError(apiError(e));
    } finally {
      setBusy(false);
    }
  };

  return (
    <>
      <tr className="border-t border-[#eceae2]" data-testid={`puzzle-row-${p.puzzle_number}`}>
        <td className="py-3 pl-4 pr-2 font-semibold tabular-nums">#{p.puzzle_number}</td>
        <td className="px-2 text-[#4a4a45]">{fmtDate(p.date)}</td>
        <td className="px-2">
          {editing ? (
            <select
              value={divisor}
              onChange={(e) => setDivisor(e.target.value)}
              className="rounded border border-[#d7d6cf] px-2 py-1 text-sm"
              data-testid={`divisor-input-${p.puzzle_number}`}
            >
              {[2, 3, 4, 5, 6, 7, 8, 9].map((d) => (
                <option key={d} value={d}>{`div ${d}`}</option>
              ))}
            </select>
          ) : (
            <span className="font-mono">÷{p.divisor}</span>
          )}
        </td>
        <td className="px-2">
          {editing ? (
            <input
              value={secret}
              onChange={(e) => setSecret(e.target.value)}
              maxLength={5}
              inputMode="numeric"
              className="w-24 rounded border border-[#d7d6cf] px-2 py-1 font-mono text-sm"
              data-testid={`answer-input-${p.puzzle_number}`}
            />
          ) : (
            <span className="font-mono tabular-nums" data-testid={`answer-${p.puzzle_number}`}>
              {p.secret}
            </span>
          )}
        </td>
        <td className="px-2">
          <span
            className={`inline-flex items-center gap-1 rounded px-2 py-0.5 text-[11px] font-semibold uppercase tracking-wide ${statusStyle[p.status]}`}
            data-testid={`status-${p.puzzle_number}`}
          >
            {locked && <Lock size={11} />}
            {locked ? "LOCKED" : p.status}
          </span>
        </td>
        <td className="px-2 py-3 pr-4 text-right whitespace-nowrap">
          {locked ? (
            <span className="text-xs text-[#a8a8a1]">—</span>
          ) : editing ? (
            <div className="inline-flex gap-2">
              <button onClick={save} disabled={busy} className="inline-flex items-center gap-1 rounded bg-[#1b1b1b] px-2.5 py-1 text-xs font-semibold text-white disabled:opacity-60" data-testid={`save-${p.puzzle_number}`}>
                <Check size={13} /> Save
              </button>
              <button onClick={() => setEditing(false)} className="inline-flex items-center gap-1 rounded border border-[#d7d6cf] px-2.5 py-1 text-xs" data-testid={`cancel-${p.puzzle_number}`}>
                <X size={13} />
              </button>
            </div>
          ) : (
            <div className="inline-flex gap-2">
              <button onClick={beginEdit} disabled={busy} className="inline-flex items-center gap-1 rounded border border-[#d7d6cf] px-2.5 py-1 text-xs hover:bg-[#f3f2ec]" data-testid={`edit-${p.puzzle_number}`}>
                <Pencil size={13} /> Edit
              </button>
              <button onClick={regen} disabled={busy} className="inline-flex items-center gap-1 rounded border border-[#d7d6cf] px-2.5 py-1 text-xs hover:bg-[#f3f2ec]" data-testid={`regenerate-${p.puzzle_number}`}>
                <RefreshCw size={13} /> Regenerate
              </button>
            </div>
          )}
        </td>
      </tr>
      {editing && (
        <tr>
          <td colSpan={6} className="bg-[#faf9f6] px-4 py-2 text-xs">
            <div className="flex flex-wrap gap-x-5 gap-y-1" data-testid={`edit-hints-${p.puzzle_number}`}>
              <Check1 ok={validLen}>Valid 5-digit number</Check1>
              <Check1 ok={divisible}>Divisible by {divisor}</Check1>
            </div>
          </td>
        </tr>
      )}
      {error && (
        <tr>
          <td colSpan={6} className="bg-red-50 px-4 py-2 text-sm text-red-600" data-testid={`edit-error-${p.puzzle_number}`}>
            {error}
          </td>
        </tr>
      )}
    </>
  );
}

export default function AdminDashboard({ onLogout, username: initialUsername = "admin" }) {
  const [puzzles, setPuzzles] = React.useState([]);
  const [loading, setLoading] = React.useState(true);
  const [error, setError] = React.useState("");
  const [query, setQuery] = React.useState("");
  const [statusFilter, setStatusFilter] = React.useState("ALL");
  const [divisorFilter, setDivisorFilter] = React.useState("ALL");
  const [settingsOpen, setSettingsOpen] = React.useState(false);
  const [username, setUsername] = React.useState(initialUsername);

  const load = React.useCallback(async () => {
    setError("");
    try {
      const data = await listPuzzles();
      setPuzzles(data);
    } catch (e) {
      setError(apiError(e));
      if (e?.response?.status === 401) onLogout();
    } finally {
      setLoading(false);
    }
  }, [onLogout]);

  React.useEffect(() => {
    load();
  }, [load]);

  const logout = () => {
    clearToken();
    onLogout();
  };

  const q = query.trim().toLowerCase();
  const filtered = puzzles.filter((p) => {
    if (statusFilter !== "ALL" && p.status !== statusFilter) return false;
    if (divisorFilter !== "ALL" && String(p.divisor) !== divisorFilter) return false;
    if (!q) return true;
    return (
      String(p.puzzle_number).includes(q) ||
      `#${p.puzzle_number}`.includes(q) ||
      p.date.includes(q) ||
      fmtDate(p.date).toLowerCase().includes(q) ||
      p.secret.includes(q)
    );
  });

  const futureCount = puzzles.filter((p) => p.status === "SCHEDULED").length;
  const selectCls = "rounded-lg border border-[#d7d6cf] bg-white px-2.5 py-2 text-xs";

  return (
    <div className="min-h-[100dvh] bg-[#faf9f6] px-4 py-8" data-testid="admin-dashboard">
      <div className="mx-auto max-w-4xl">
        <header className="mb-6 flex items-center justify-between">
          <div>
            <h1 className="text-xl font-extrabold tracking-[0.16em] pl-[0.16em]">NUMEX</h1>
            <p className="text-xs uppercase tracking-widest text-[#75756f]">Puzzle Admin</p>
          </div>
          <div className="flex items-center gap-2">
            <button onClick={async () => { await generateBuffer(); load(); }} className="rounded-lg border border-[#d7d6cf] px-3 py-2 text-xs font-medium hover:bg-white" data-testid="admin-generate">
              Generate buffer
            </button>
            <button onClick={() => setSettingsOpen(true)} className="inline-flex items-center gap-1 rounded-lg border border-[#d7d6cf] px-3 py-2 text-xs font-medium hover:bg-white" data-testid="admin-settings-btn">
              <Settings size={14} /> Settings
            </button>
            <button onClick={logout} className="rounded-lg border border-[#d7d6cf] px-3 py-2 text-xs font-medium hover:bg-white" data-testid="admin-logout">
              Sign out
            </button>
          </div>
        </header>

        {/* Search / filter */}
        <div className="mb-3 flex flex-wrap items-center gap-2">
          <div className="relative flex-1 min-w-[200px]">
            <Search size={15} className="absolute left-3 top-1/2 -translate-y-1/2 text-[#a8a8a1]" />
            <input
              value={query}
              onChange={(e) => setQuery(e.target.value)}
              placeholder="Search by #, date or answer…"
              className="w-full rounded-lg border border-[#d7d6cf] bg-white py-2 pl-9 pr-3 text-sm outline-none focus:border-[#1b1b1b]"
              data-testid="admin-search"
            />
          </div>
          <select value={statusFilter} onChange={(e) => setStatusFilter(e.target.value)} className={selectCls} data-testid="filter-status">
            <option value="ALL">All statuses</option>
            <option value="LIVE">Live</option>
            <option value="SCHEDULED">Scheduled</option>
            <option value="COMPLETED">Completed</option>
          </select>
          <select value={divisorFilter} onChange={(e) => setDivisorFilter(e.target.value)} className={selectCls} data-testid="filter-divisor">
            <option value="ALL">All divisors</option>
            {[2, 3, 4, 5, 6, 7, 8, 9].map((d) => (
              <option key={d} value={String(d)}>{`div ${d}`}</option>
            ))}
          </select>
        </div>

        <p className="mb-3 text-sm text-[#75756f]" data-testid="future-count">
          {futureCount} future scheduled puzzles · {filtered.length} shown
        </p>

        {error && <p className="mb-4 text-sm text-red-600">{error}</p>}

        <div className="overflow-x-auto rounded-xl border border-[#e4e3dc] bg-white">
          <table className="w-full text-left text-sm">
            <thead>
              <tr className="text-[11px] uppercase tracking-wider text-[#75756f]">
                <th className="py-3 pl-4 pr-2">Puzzle #</th>
                <th className="px-2">Date</th>
                <th className="px-2">Divisor</th>
                <th className="px-2">Answer</th>
                <th className="px-2">Status</th>
                <th className="px-2 pr-4 text-right">Actions</th>
              </tr>
            </thead>
            <tbody>
              {loading ? (
                <tr><td colSpan={6} className="p-6 text-center text-[#75756f]">Loading…</td></tr>
              ) : filtered.length === 0 ? (
                <tr><td colSpan={6} className="p-6 text-center text-[#75756f]">No puzzles match.</td></tr>
              ) : (
                filtered.map((p) => <Row key={p.puzzle_number} p={p} onChanged={load} />)
              )}
            </tbody>
          </table>
        </div>
      </div>

      <AdminSettings
        open={settingsOpen}
        onOpenChange={setSettingsOpen}
        username={username}
        onUsernameChanged={setUsername}
      />
    </div>
  );
}
